import { Formik } from "formik";
import { toastr } from "react-redux-toastr";
import { debounce, isEmpty, size } from "lodash";
import React, { useMemo, useState } from "react";
import { Box, Typography } from "@material-ui/core";
import { useParams, useHistory } from "react-router-dom";
// component
import BackIcon from "assets/icons/Back.png";
import MyButton from "components/button/Button";
import { useApiAction } from "hook/useFetchData";
import FinanceService from "Lib/API/Finance/index";
import PreSchoolService from "Lib/API/PreSchool/index";
// direct import
import Header from "./view/header/index";
import { cancelButton, submitButton } from "./helper";
import RenewalTableListing from "./view/table-listing";

const RenewalForm = () => {
  const { id } = useParams();
  const history = useHistory();
  const [loading] = useState(false);
  const [page, setPage] = useState(1);
  const [activeFilterBody, setActiveFilterBody] = useState({});
  const [selectedAccounts, setSelectedAccounts] = useState([]);

  const {
    execute: fetchWithDelay,
    loading: tableLoading,
    data: tableData,
    setData: setListingData,
  } = useApiAction((token, currentPage, body) =>
    FinanceService.getListofAccounts(token, currentPage, 50, body)
  );
  const { execute: sendInvitation, loading: invitationLoading } = useApiAction(
    (token, body) => PreSchoolService.createInvitation(token, body)
  );

  const sortCharged = (field) => {
    console.log("Sorting by:", field);
  };

  // Handle individual checkbox click
  const onCheckboxClick = (e, row) => {
    setSelectedAccounts((prev) => {
      const exists = prev.includes(row._id);
      if (exists) {
        return prev.filter((id) => id !== row._id);
      } else {
        return [...prev, row._id];
      }
    });
  };

  // Handle "select all" checkbox for current page
  const onSelectAllClick = () => {
    setSelectedAccounts((prev) => {
      if (prev.includes("all")) {
        return [];
      } else {
        return ["all"];
      }
    });
  };

  const handleFilter = (values) => {
    setListingData([]);
    setPage(1);
    const body = {
      ...(values?.account_name && { search: values?.account_name }),
      filter: {
        ...(values?.account_type && {
          account_type: values?.account_type?.id?.toLowerCase(),
        }),
        ...(values?.category && { category: values?.category?.name }),
        ...(values?.billing_type && {
          billing_type: [values?.billing_type?.name],
        }),
        ...(values?.accountTags && { accountTags: values?.accountTags }),
        ...(values?.contactTags && { contactTags: values?.contactTags }),
        ...(values?.accountNoTag && { accountNoTag: values?.accountNoTag }),
        ...(values?.contactNoTag && { contactNoTag: values?.contactNoTag }),
      },
    };
    setActiveFilterBody(body);
    fetchWithDelay(page, body);
  };
  const handleReset = (reset) => {
    reset();
    setPage(1);
    setActiveFilterBody({});
    setListingData([]);
    fetchWithDelay(page);
  };
  const debouncedHandleFilter = useMemo(
    () => debounce(handleFilter, 1500),
    [handleFilter]
  );

  const handleSearch = (e) =>
    debouncedHandleFilter({ account_name: e?.target.value });

  return (
    <Formik
      initialValues={{
        account_name: "",
        account_type: null,
        billing_type: null,
        category: null,
        accountTags: null,
        contactTags: null,
        accountNoTag: null,
        contactNoTag: null,
      }}
      onSubmit={async (values) => {
        const getIsAll = selectedAccounts.includes("all");

        const {
          account_name,
          account_type,
          category,
          billing_type,
          accountTags,
          contactTags,
          accountNoTag,
          contactNoTag,
        } = values || {};

        const filter = {
          accountsIds: selectedAccounts,
          ...(getIsAll && account_type && { account_type: account_type.id }),
          ...(getIsAll && category && { category: category?.name }),
          ...(getIsAll &&
            billing_type && { billing_type: [billing_type?.name] }),
          ...(getIsAll && accountTags && { accountTags }),
          ...(getIsAll && contactTags && { contactTags }),
          ...(getIsAll && accountNoTag && { accountNoTag }),
          ...(getIsAll && contactNoTag && { contactNoTag }),
        };

        const body = {
          formId: id,
          ...(getIsAll && account_name && { search: account_name }),
          filter,
        };

        try {
          await sendInvitation(body);
          toastr.success("Success", "The form is sent successfully");
          history.push(`/forms?key=membership`);
        } catch (error) {
          toastr.error(
            "Error",
            "No valid contacts found, could not send invitation"
          );
        }
      }}
    >
      {(formik) => (
        <Box sx={{ width: "100%", pr: 4 }}>
          <Box
            sx={{
              marginTop: 40,
              marginBottom: 30,
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
            }}
          >
            <img
              src={BackIcon}
              alt="Back"
              onClick={() => history.push("/forms?key=membership")}
            />
            <Typography
              variant="h4"
              gutterBottom
              style={{
                fontWeight: 500,
                fontSize: 24,
                color: "#2455ef",
                marginLeft: 10,
              }}
            >
              Renewal Form
            </Typography>
          </Box>

          <Box sx={{ width: "100%" }} className="family-table-container">
            <Header
              handleFilter={handleFilter}
              value={formik.values}
              reset={formik.resetForm}
              handleReset={handleReset}
              tableLoading={tableLoading}
              handleSearch={handleSearch}
            />
            {!isEmpty(tableData?.payload?.data) ? (
              <>
                <RenewalTableListing
                  setPage={setPage}
                  sortCharged={sortCharged}
                  count={tableData?.payload?.pagination?.count}
                  page={page}
                  productData={tableData?.payload?.data}
                  loading={loading || tableLoading}
                  selectedData={selectedAccounts}
                  onCheckboxClick={onCheckboxClick}
                  onSelectAllClick={onSelectAllClick}
                  itemsPerPage={50}
                  fetchWithDelay={fetchWithDelay}
                  activeFilterBody={activeFilterBody}
                />
                <Box
                  display="flex"
                  justifyContent="flex-end"
                  sx={{ mt: 3, p: 4 }}
                >
                  <MyButton
                    onClick={() => history.push(`/forms`)}
                    style={cancelButton}
                    type="button"
                    disabled={tableLoading || formik?.isSubmitting}
                  >
                    Cancel
                  </MyButton>

                  <MyButton
                    onClick={() => formik.submitForm()}
                    style={submitButton}
                    type="button"
                    disabled={
                      tableLoading ||
                      size(selectedAccounts) === 0 ||
                      formik?.isSubmitting
                    }
                  >
                    Submit
                  </MyButton>
                </Box>
              </>
            ) : (
              <Box display="flex" justifyContent="center">
                List is empty
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Formik>
  );
};

export default RenewalForm;
